import { BehaviorSubject, Observable } from 'rxjs';
import { AccessRecipe } from 'src/app/hickory-api';
import { DirectoryCache } from './directory-cache';
import { DirectoryGroup, DirectoryUser, DirectoryType, PagedDirectoryUsers, PagedDirectoryGroups } from './models';
import { RequestCache } from "../../cache/request.cache";
import { AlertModelService } from 'src/app/alert/shared/alert-model';

export abstract class BaseDirectoryService {
  directoryId: string = undefined;
  directoryType: DirectoryType = DirectoryType.Unsupported;
  splitDirectory: boolean = false;

  // use a singleton shared cache for directory object caching
  private readonly cache = DirectoryCache.getInstance();

  private groupRequestsInProgress = 0;
  inProgressGroups$ = new BehaviorSubject<boolean>(false);
  groupData$ = new BehaviorSubject<DirectoryGroup[]>(null);
  private userRequestsInProgress = 0;
  inProgressUsers$ = new BehaviorSubject<boolean>(false);
  userData$ = new BehaviorSubject<DirectoryUser[]>(null);
  protected countRequestMap = new Map<string, number>();

  get cachedGroups(): DirectoryGroup[] { return this.cache.getAllGroups(this.directoryId); }
  get cachedUsers(): DirectoryUser[] { return this.cache.getAllUsers(this.directoryId); }
  get isInProgressGroups(): boolean { return this.groupRequestsInProgress > 0; }
  get isInProgressUsers(): boolean { return this.userRequestsInProgress > 0; }
  get countRequestCache(): RequestCache<number> { return this.cache.countRequestCache; }

  incrUserRequests() { this.userRequestsInProgress++; this.inProgressUsers$.next(this.isInProgressUsers); }
  decrUserRequests() { this.userRequestsInProgress = Math.max(0, this.userRequestsInProgress - 1); this.inProgressUsers$.next(this.isInProgressUsers); }

  putUser(item: DirectoryUser) { this.cache.putUser(item); }
  getCachedUser(id: number | string): DirectoryUser { return this.cache.getUser(id); }

  incrGroupRequests() { this.groupRequestsInProgress++; this.inProgressGroups$.next(this.isInProgressGroups); }
  decrGroupRequests() { this.groupRequestsInProgress = Math.max(0, this.groupRequestsInProgress - 1); this.inProgressGroups$.next(this.isInProgressGroups); }

  putGroup(item: DirectoryGroup) {  this.cache.putGroup(item); }
  getCachedGroup(id: number | string): DirectoryGroup { return this.cache.getGroup(id); }

  loadGroupData(): Observable<DirectoryGroup[]> {
    this.getRootGroups();
    return this.groupData$.asObservable();
  }

  reset() {
    this.groupRequestsInProgress = 0;
    this.inProgressGroups$.next(false);
    this.groupData$.next(null);
    this.userRequestsInProgress = 0;
    this.inProgressUsers$.next(false);
    this.userData$.next(null);
    this.countRequestCache.clear();
  }

  abstract getRootGroups();
  abstract getGroup(id: string, refresh?: boolean): Observable<DirectoryGroup>;
  abstract getGroupChildren(id: string): Observable<DirectoryGroup[]>;
  abstract getGroupMembers(id: string): Observable<DirectoryUser[]>;
  abstract searchGroups(query: string): Observable<DirectoryGroup[]>;
  abstract getGroupsById(ids: string[], page?: number, pageSize?: number): Observable<PagedDirectoryGroups>;

  abstract getUserCountBySearch(search: AccessRecipe[]): Observable<number>;
  abstract getUsersBySearch(search: AccessRecipe[], page?: number, pageSize?: number, sort?: string): Observable<PagedDirectoryUsers>;
  abstract getUsersById(ids: string[], groupIds?: string[], page?: number, pageSize?: number, sort?: string): Observable<PagedDirectoryUsers>;
  abstract getUsers(query?: string, page?: number, pageSize?: number): Observable<PagedDirectoryUsers>;

  abstract totalRecipientCount(alertModel: AlertModelService): Observable<number>;
}
